import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChatMessage, ChatMessageDocument, ChatThread } from '@models';
import { API_URL } from '@services';
import { BYPASS_AUTH_RECOVERY } from '../http.service';
import { pollResumingAfterTokenRefresh } from '../poll-resuming-after-token-refresh';
import { RefreshTokenStateService } from '../refresh-token-state.service';
import { ChatService } from './chat.service';

interface ChatDocumentApiResponse {
  documentId: string;
  name: string;
  sizeBytes: number;
  format: string;
}

interface ChatMessageApiResponse {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  sentAt: string;
  document?: ChatDocumentApiResponse | null;
}

interface ChatThreadApiResponse {
  threadId: string;
  counterparty: {
    userId: string;
    patientId: string;
    role: string;
    givenName: string;
    familyName: string;
    avatarUrl: string | null;
    email: string | null;
  };
  lastMessagePreview: string;
  lastMessageAt: string;
  unreadCount: number;
}

const POLL_INTERVAL_MS = 5000;
// Bound the HTTP transport without cancelling jwtInterceptor token recovery.
const CHAT_READ_TIMEOUT_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class ChatApiService implements ChatService {
  private readonly http = inject(HttpClient);
  private readonly apiRootUrl = inject(API_URL);
  private readonly refreshTokenState = inject(RefreshTokenStateService, {
    optional: true,
  });

  // Passive polling ticks must not trigger the jwtInterceptor's /auth/refresh-token
  // recovery: refreshing bumps the session's last-activity timestamp, which would
  // keep an idle user logged in forever. See poll() for when this is applied.
  // Goes away with phase 2 (real-time streams).
  private readonly bypassAuthRecoveryContext = new HttpContext().set(
    BYPASS_AUTH_RECOVERY,
    true
  );

  getThreads(options?: {
    bypassAuthRecovery?: boolean;
  }): Observable<ChatThread[]> {
    return this.http
      .get<ChatThreadApiResponse[]>(`${this.apiRootUrl}/chat/threads`, {
        timeout: CHAT_READ_TIMEOUT_MS,
        context: options?.bypassAuthRecovery
          ? this.bypassAuthRecoveryContext
          : undefined,
      })
      .pipe(
        map(threads =>
          threads.map(t => ({ ...t, lastMessageAt: new Date(t.lastMessageAt) }))
        )
      );
  }

  getMessages(
    threadId: string,
    options?: { bypassAuthRecovery?: boolean }
  ): Observable<ChatMessage[]> {
    const url = `${this.apiRootUrl}/chat/threads/${encodeURIComponent(threadId)}/messages`;
    return this.http
      .get<ChatMessageApiResponse[]>(url, {
        timeout: CHAT_READ_TIMEOUT_MS,
        context: options?.bypassAuthRecovery
          ? this.bypassAuthRecoveryContext
          : undefined,
      })
      .pipe(map(msgs => msgs.map(m => this.mapMessage(m))));
  }

  private mapMessage(response: ChatMessageApiResponse): ChatMessage {
    const document: ChatMessageDocument | undefined = response.document
      ? {
          id: response.document.documentId,
          name: response.document.name,
          sizeBytes: response.document.sizeBytes,
          format: response.document.format,
        }
      : undefined;
    return {
      id: response.id,
      senderId: response.senderId,
      senderName: response.senderName,
      text: response.text,
      sentAt: new Date(response.sentAt),
      document,
    };
  }

  // TODO phase 2: replace timer polling with a backend-mediated push mechanism (chat stays
  // backend-mediated; clients never talk to ACS directly).
  watchMessages(threadId: string): Observable<ChatMessage[]> {
    return this.poll(bypassAuthRecovery =>
      this.getMessages(threadId, { bypassAuthRecovery })
    );
  }

  watchThreads(): Observable<ChatThread[]> {
    return this.poll(bypassAuthRecovery =>
      this.getThreads({ bypassAuthRecovery })
    );
  }

  private poll<T>(
    request: (bypassAuthRecovery: boolean) => Observable<T>
  ): Observable<T> {
    return pollResumingAfterTokenRefresh(
      POLL_INTERVAL_MS,
      request,
      this.refreshTokenState
    );
  }

  sendMessage(threadId: string, text: string): Observable<void> {
    const url = `${this.apiRootUrl}/chat/threads/${encodeURIComponent(threadId)}/messages`;
    return this.http.post<void>(url, { text });
  }

  markThreadAsRead(threadId: string): Observable<void> {
    const url = `${this.apiRootUrl}/chat/threads/${encodeURIComponent(threadId)}/read`;
    return this.http.post<void>(url, {});
  }
}
